import React, { useState } from "react";

import { useGetScenarioResilienceInsight, useTranslation } from "utils";

import { NO_VALUE, RESILIENCE_UNLOCK_MIN_CROPS } from "consts";

import { AlertBanner, Button, Icon, Skeleton, Tooltip, Typography } from "components";

import ResilienceScoreDonutChart from "pages/app/resilience-score/components/resilience-score-context-drawer/ResilienceScoreDonutChart";
import { HorizontalScrollFade } from "pages/app/scenario-analysis/components/HorizontalScrollFade";
import { ResilienceScoreDrawer } from "pages/app/scenario-analysis/components/resilience-score-drawer/ResilienceScoreDrawer";
import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { scenarioLineColorAt } from "pages/app/scenario-analysis/utils/forecast";
import {
  buildResilienceInsightRequest,
  countFilledCropYears,
  getResilienceDelta,
  getResilienceTier,
  scoreToFraction,
  soilCoverPercentFromScore,
  toInsightLocale,
  topOpportunity,
} from "pages/app/scenario-analysis/utils/resilienceScorecard";
import { ScenarioResilienceByScenarioId, ScenarioResilienceResult, hasRotationMetrics } from "pages/app/scenario-analysis/utils/scenarioResilienceScore";

type Props = {
  scenarios: ScenarioData[];
  resilienceData: ScenarioResilienceByScenarioId;
  activeScenarioId: string | null;
  onSelectScenario: (scenarioId: string) => void;
  /** Opens the scenario builder card for editing — used by the "not enough crops" empty state. */
  onEditScenario?: (scenarioId: string) => void;
  /** False while outcomes/scores are stale (dirty / calculating) — skips the AI insight fetch. */
  scoresFresh?: boolean;
  dataTestId?: string;
};

type SubScorePillar = "soilHealth" | "waterManagement" | "biodiversity";

const SubScoreBar = ({
  labelKey,
  tooltipKey,
  value,
  pillar,
  dataTestId,
}: {
  labelKey: string;
  tooltipKey: string;
  value: number;
  pillar: SubScorePillar;
  dataTestId: string;
}) => {
  const { t } = useTranslation("scenarioAnalysis");
  const width = `${scoreToFraction(value) * 100}%`;
  return (
    <div className={`resilience-scorecard__subscore resilience-scorecard__subscore--${pillar}`} data-testid={dataTestId}>
      <div className="resilience-scorecard__subscore-head">
        <Typography variant="paragraph-s" className="resilience-scorecard__subscore-label">
          {t(labelKey)}
        </Typography>
        <Tooltip title={t(tooltipKey)} placement="top">
          <span className="resilience-scorecard__subscore-info" aria-label={t(tooltipKey)} data-testid={`${dataTestId}-info`}>
            <Icon iconName="tabler/info-circle" size="s" />
          </span>
        </Tooltip>
      </div>
      <div className="resilience-scorecard__subscore-track">
        <div className="resilience-scorecard__subscore-fill" style={{ width }} />
      </div>
      <Typography variant="paragraph-s" bold className="resilience-scorecard__subscore-value">
        {value.toFixed(1)}
      </Typography>
    </div>
  );
};

const MetricTile = ({ value, labelKey, dataTestId }: { value: string; labelKey: string; dataTestId: string }) => {
  const { t } = useTranslation("scenarioAnalysis");
  return (
    <div className="resilience-scorecard__metric" data-testid={dataTestId}>
      <Typography variant="h5" className="resilience-scorecard__metric-value">
        {value}
      </Typography>
      <Typography variant="paragraph-xs" color="grey-600" className="resilience-scorecard__metric-label">
        {t(labelKey)}
      </Typography>
    </div>
  );
};

export const ResilienceScorecard = ({
  scenarios,
  resilienceData,
  activeScenarioId,
  onSelectScenario,
  onEditScenario,
  scoresFresh = true,
  dataTestId = "resilience-scorecard",
}: Props) => {
  const { t, i18n } = useTranslation("scenarioAnalysis");
  const [showDrawer, setShowDrawer] = useState(false);

  const baseline = scenarios.find((scenario) => scenario.isBaseline) ?? scenarios[0];
  const activeScenario = scenarios.find((scenario) => scenario.id === activeScenarioId) ?? baseline;

  // insight hook must run before the early return
  const activeResult: ScenarioResilienceResult | undefined = activeScenario ? resilienceData[activeScenario.id] : undefined;
  const baselineResult = baseline ? resilienceData[baseline.id] : undefined;
  // Slot-count gate, shared with the empty state and the API's own rule — not distinct crops.
  const hasEnoughCrops = countFilledCropYears(activeScenario?.years) >= RESILIENCE_UNLOCK_MIN_CROPS;
  const delta =
    activeResult && baselineResult && activeScenario && !activeScenario.isBaseline
      ? getResilienceDelta(activeResult.baseScore, baselineResult.baseScore)
      : null;
  const insightRequest =
    activeScenario && activeResult && hasEnoughCrops && scoresFresh
      ? buildResilienceInsightRequest(activeScenario.years, activeResult, activeScenario.isBaseline, toInsightLocale(i18n.language), delta?.value)
      : null;
  const { data: insightData, isLoading: isInsightLoading } = useGetScenarioResilienceInsight({
    scenarioId: activeScenario?.id ?? "",
    requestBody: insightRequest,
  });

  if (!activeScenario) return null;

  const activeIndex = scenarios.findIndex((scenario) => scenario.id === activeScenario.id);
  const activeColor = scenarioLineColorAt(activeIndex);

  const tier = activeResult ? getResilienceTier(activeResult.baseScore) : null;

  // Narrow to a value (not a boolean): a plain boolean would discard the `hasRotationMetrics` type
  // predicate, leaving the four metric fields as `number | undefined` at the tiles.
  const rotationMetrics = activeResult && hasRotationMetrics(activeResult) ? activeResult : null;

  return (
    <div className="resilience-scorecard" data-testid={dataTestId}>
      <div className="resilience-scorecard__card">
        <HorizontalScrollFade className="resilience-scorecard__tabs-scroll" dataTestId={`${dataTestId}-tabs-scroll`}>
          <div className="resilience-scorecard__tabs" role="tablist" aria-label={t("resilienceScorecard.tabsLabel")}>
            {scenarios.map((s, idx) => {
              const isActive = s.id === activeScenario.id;
              const color = scenarioLineColorAt(idx);
              const result = resilienceData[s.id];
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`resilience-scorecard__tab${isActive ? " resilience-scorecard__tab--active" : ""}`}
                  onClick={() => onSelectScenario(s.id)}
                  data-testid={`${dataTestId}-tab-${s.id}`}
                >
                  <span className="resilience-scorecard__tab-dot" style={{ backgroundColor: color }} aria-hidden />
                  <span className={`resilience-scorecard__tab-name${isActive ? " resilience-scorecard__tab-name--active" : ""}`} title={s.title}>
                    {s.title}
                  </span>
                  {result ? (
                    <span className={`resilience-scorecard__tab-score${isActive ? " resilience-scorecard__tab-score--active" : ""}`}>
                      {result.baseScore.toFixed(1)}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </HorizontalScrollFade>

        <div className="resilience-scorecard__panel" role="tabpanel" data-testid={`${dataTestId}-panel`}>
          {hasEnoughCrops ? (
            <>
              <div className="resilience-scorecard__hero">
                <div className="resilience-scorecard__hero-score-block">
                  <div className="resilience-scorecard__hero-donut">
                    <ResilienceScoreDonutChart progressPct={activeResult?.baseScore ?? 0} color={activeColor} />
                  </div>
                  <div className="resilience-scorecard__hero-score">
                    <Typography variant="hero" className="resilience-scorecard__hero-value">
                      {activeResult ? activeResult.baseScore.toFixed(1) : NO_VALUE}
                    </Typography>
                    <Typography variant="h3" color="grey-600" className="resilience-scorecard__hero-max">
                      {t("resilienceScorecard.scoreOutOf")}
                    </Typography>
                  </div>
                </div>

                <div className="resilience-scorecard__hero-meta">
                  <div className="resilience-scorecard__hero-title-row">
                    <span className="resilience-scorecard__hero-dot" style={{ backgroundColor: activeColor }} aria-hidden />
                    <Typography variant="h6" className="resilience-scorecard__hero-name">
                      {activeScenario.title}
                    </Typography>
                    {tier ? (
                      <Typography
                        variant="paragraph-xs"
                        bold
                        className={`resilience-scorecard__tier-chip resilience-scorecard__tier-chip--${tier.key}`}
                        data-testid={`${dataTestId}-tier`}
                      >
                        {t(tier.labelKey)}
                      </Typography>
                    ) : null}
                    {delta && (
                      <Typography
                        variant="paragraph-xs"
                        className={`resilience-scorecard__delta resilience-scorecard__delta--${delta.tone}`}
                        data-testid={`${dataTestId}-delta`}
                      >
                        <Icon iconName={delta.tone === "positive" ? "tabler/trending-up" : "tabler/trending-down"} size="s" />
                        {t("resilienceScorecard.deltaBadge", { delta: delta.formatted })}
                      </Typography>
                    )}
                  </div>
                  {tier ? (
                    <Typography variant="paragraph-s" className="resilience-scorecard__hero-interpretation">
                      {t(tier.interpretationKey)}
                    </Typography>
                  ) : null}
                </div>
              </div>

              <div className="resilience-scorecard__body">
                {activeResult ? (
                  <div className="resilience-scorecard__subscores" data-testid={`${dataTestId}-subscores`}>
                    <SubScoreBar
                      labelKey="resilienceScorecard.subScore.soilHealth.label"
                      tooltipKey="resilienceScorecard.subScore.soilHealth.tooltip"
                      value={activeResult.breakdown.soilHealth}
                      pillar="soilHealth"
                      dataTestId={`${dataTestId}-subscore-soilHealth`}
                    />
                    <SubScoreBar
                      labelKey="resilienceScorecard.subScore.waterManagement.label"
                      tooltipKey="resilienceScorecard.subScore.waterManagement.tooltip"
                      value={activeResult.breakdown.waterCapacity}
                      pillar="waterManagement"
                      dataTestId={`${dataTestId}-subscore-waterManagement`}
                    />
                    <SubScoreBar
                      labelKey="resilienceScorecard.subScore.biodiversity.label"
                      tooltipKey="resilienceScorecard.subScore.biodiversity.tooltip"
                      value={activeResult.breakdown.biodiversity}
                      pillar="biodiversity"
                      dataTestId={`${dataTestId}-subscore-biodiversity`}
                    />
                  </div>
                ) : null}

                {rotationMetrics ? (
                  <div className="resilience-scorecard__metrics" data-testid={`${dataTestId}-metrics`}>
                    <MetricTile
                      value={String(rotationMetrics.cropsInRotation)}
                      labelKey="resilienceScorecard.metric.cropsInRotation"
                      dataTestId={`${dataTestId}-metric-crops`}
                    />
                    <MetricTile
                      value={String(rotationMetrics.plantFamilies)}
                      labelKey="resilienceScorecard.metric.plantFamilies"
                      dataTestId={`${dataTestId}-metric-families`}
                    />
                    <MetricTile
                      value={`${soilCoverPercentFromScore(rotationMetrics.soilCoverScore)}%`}
                      labelKey="resilienceScorecard.metric.soilCover"
                      dataTestId={`${dataTestId}-metric-soil-cover`}
                    />
                    <MetricTile
                      value={`${rotationMetrics.coverCropSharePercent}%`}
                      labelKey="resilienceScorecard.metric.coverCropShare"
                      dataTestId={`${dataTestId}-metric-cover-crop-share`}
                    />
                  </div>
                ) : null}
              </div>

              <AlertBanner
                className="resilience-scorecard__insight"
                type="#f1f6ff"
                iconName="tabler/bulb"
                iconSize={24}
                iconAlign="start"
                description={
                  isInsightLoading ? (
                    <Skeleton type="paragraph" width="100%" rows={1} />
                  ) : (
                    // On error/empty AI response, fall back silently to the rule-based lever... same priority order as the prompt.
                    (scoresFresh && insightData?.insight?.trim()) ||
                    (activeResult ? t(`resilienceScorecard.topOpportunity.${topOpportunity(activeResult)}`) : t("resilienceScorecard.insight"))
                  )
                }
                descriptionSize="paragraph-m"
                dataTestId={`${dataTestId}-insight`}
              />
            </>
          ) : (
            <div className="resilience-scorecard__empty" data-testid={`${dataTestId}-empty`}>
              <Icon iconName="klim/crop-footprint" size="xl" className="resilience-scorecard__empty-icon" />
              <Typography variant="h6" className="resilience-scorecard__empty-title">
                {t("resilienceScorecard.emptyState.title")}
              </Typography>
              <Typography variant="paragraph-m" color="grey-600" className="resilience-scorecard__empty-description">
                {t("resilienceScorecard.emptyState.description")}
              </Typography>
              {onEditScenario ? (
                <Button
                  type="primary"
                  onClick={() => onEditScenario(activeScenario.id)}
                  className="resilience-scorecard__empty-cta"
                  dataTestId={`${dataTestId}-empty-edit`}
                >
                  {t("resilienceScorecard.emptyState.editScenario")}
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <div className="resilience-scorecard__footer">
        <Typography variant="paragraph-s" className="resilience-scorecard__methodology">
          {t("resilienceScorecard.methodology")}
        </Typography>
        <button type="button" className="resilience-scorecard__learn-more" onClick={() => setShowDrawer(true)} data-testid={`${dataTestId}-learn-more`}>
          {t("resilienceScorecard.learnMore")}
        </button>
      </div>

      <ResilienceScoreDrawer visible={showDrawer} onClose={() => setShowDrawer(false)} dataTestId={`${dataTestId}-drawer`} />
    </div>
  );
};

export default ResilienceScorecard;
